import { Collection, MaxString, ModelBase, NormalString, ObservableCollection, Property, Required, LongString, VeryLongString } from '@skylab-shared/core';
import { DdkState } from '../accountancy/ddk-state';
import { CalculationMethod } from '../common/calculation-method';
import { EppState } from '../common/epp-state';
import { RelatedEntity } from '../common/related-entity';
import { DocumentBuyerModel } from '../document/document-buyer.model';
import { DocumentClientModel } from '../document/document-client.model';
import { DocumentPaymentListItemModel } from '../document/document-payment-list-item.model';
import { ExchangeRateModel } from '../exchange-rate/exchange-rate.model';
import { InventoryCostCalculationMethod } from '../inventory/inventory-cost-calculation-method';
import { InventoryEffect } from '../inventory/inventory-effect';
import { KsefSendSubmissionMode } from '../ksef/ksef-send-submission-mode';
import { SplitPaymentState } from '../sale/split-payment-state';
import { VatTableItemModel } from '../vat-table/vat-table-item.model';
import { PurchaseBankAccountModel } from './purchase-bank-account.model';
import { PurchaseDocumentKind } from './purchase-document-kind';
import { PurchaseInvoiceDocumentItemModel } from './purchase-invoice-document-item.model';

export class PurchaseInvoiceDocumentModel extends ModelBase {
    @Property({ identifiable: true })
    public id: number;

    @Property()
    public calculationMethod: CalculationMethod.Net | CalculationMethod.Gross | CalculationMethod.Manual;

    @Property()
    public favourite: boolean;

    @Property()
    public cashBasis: boolean;

    @Property(String, {nullable: true})
    public ksefDocumentId: string | null;

    @Property({ nullable: false })
    public ksefSubmissionMode: KsefSendSubmissionMode;

    @Property(Number, { nullable: true })
    public ksefHeaderId?: number | null;

    @Property({ nullable: true })
    public dateOfAssigningKsefNumber?: Date;

    @Property(String, { nullable: true })
    public ksefHash?: string | null;

    @Property(String, { nullable: true })
    public ksefCollectiveIdentifier?: string | null;

    @Property()
    @Required()
    public costCalculationMethod: InventoryCostCalculationMethod;

    @Property(DocumentClientModel, { nullable: true })
    public seller: DocumentClientModel | null;

    @Property(DocumentBuyerModel, { nullable: true })
    public supplier: DocumentBuyerModel | null;

    @Property(PurchaseBankAccountModel, { nullable: true })
    public bankAccount: PurchaseBankAccountModel | null;

    @Property()
    public communityTransactionId: number;

    @Property(String, {nullable: true})
    @Required('data-model.document.validation.transaction-location.non-empty')
    @LongString('data-model.document.validation.transaction-location.length')
    public transactionLocation: string | null;

    @Property()
    public currencyId: number;

    @Property(Date, {nullable: true})
    public issueDate: Date | null;

    @Property()
    public deliveryDate: Date;

    @Property(Date, {nullable: true})
    public receiptDate: Date | null;

    @Property({ nullable: true })
    public vatDeductionDate: Date;

    @Property()
    public grossValue: number;

    @Property(Number, {nullable: true})
    public currencyGrossValue: number | null;

    @Property()
    public netValue: number;

    @Property()
    @VeryLongString('data-model.purchase.validation.document-number.length')
    public documentNumber: string;

    @Property(Date, { nullable: true })
    public dueDate: Date | null;

    @Property(Number, {nullable: true})
    public dueDateDays: number | null;

    @Collection({ type: DocumentPaymentListItemModel })
    public payments: ObservableCollection<DocumentPaymentListItemModel>;

    @Property(ExchangeRateModel, { nullable: true })
    public exchangeRate: ExchangeRateModel | null;

    @Property(String, {nullable: true})
    @VeryLongString('data-model.document.validation.expense-name.length')
    public expenseName: string | null;

    @Property()
    @VeryLongString('data-model.document.validation.external-document-number.length')
    public externalDocumentNumber: string;

    @Property()
    public fixedAsset: boolean;

    @Property()
    public issuedByVatPayer: boolean;

    @Property()
    public jpkVatGroups: string[];

    @Property()
    public readonly kind: PurchaseDocumentKind = PurchaseDocumentKind.PurchaseInvoice;

    @Property()
    @MaxString('data-model.document.validation.note.length')
    public note: string;


    @Collection({ type: PurchaseInvoiceDocumentItemModel })
    public items: ObservableCollection<PurchaseInvoiceDocumentItemModel>;

    @Property()
    public numberingId: number;

    @Property()
    @Required('data-model.purchase.validation.payment-method.required')
    public paymentMethodId: number;

    @Property()
    public splitPayment: SplitPaymentState;

    @Collection({ type: VatTableItemModel })
    public vatTableItems: ObservableCollection<VatTableItemModel>;

    @Property(String, {nullable: true})
    public recipient: string | null;

    @Property(String, {nullable: true})
    @NormalString('data-model.purchase.validation.issuer.length')
    public issuer: string | null;

    @Property()
    public relatedInventoryDocuments: RelatedEntity[];

    @Property()
    public includesInventoryControl: boolean;

    @Property()
    public automaticVatCalculation: boolean;

    @Property(Number, {nullable: true})
    public inventoryEffect: InventoryEffect | null;

    @Property()
    public ddkState: DdkState;

    @Property()
    public eppState: EppState;

    @Property({nullable: true})
    public originalEInvoiceDocumentId?: number;
}
